import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MergeType as MergeIcon,
  MenuBook as BookIcon,
} from '@mui/icons-material';
import { PageHeader, DataTable } from '../components/common';
import { adminApi } from '../utils/api';
import { format } from 'date-fns';

const Books = () => {
  const theme = useTheme();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [formData, setFormData] = useState({ name: '', author: '' });

  const fetchBooks = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await adminApi.getBooks({
        search,
        page: page + 1,
        limit: pageSize,
      });
      setBooks(data.books || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error('Failed to fetch books:', err);
      // Show empty state on error - no mock data
      setBooks([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize]);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  const handleEdit = (book) => {
    setEditingBook(book);
    setFormData({ name: book.name, author: book.author || '' });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      await adminApi.updateBook(editingBook.id, formData);
      fetchBooks();
      setEditDialogOpen(false);
    } catch (err) {
      console.error('Failed to update book:', err);
    }
  };

  const handleDelete = async (bookId) => {
    if (window.confirm('Are you sure you want to delete this book? All recipes will be unlinked.')) {
      try {
        await adminApi.deleteBook(bookId);
        fetchBooks();
      } catch (err) {
        console.error('Failed to delete book:', err);
      }
    }
  };

  const handleMerge = async () => {
    if (selected.length !== 2) return;
    try {
      await adminApi.mergeBooks(selected[0], selected[1]);
      setSelected([]);
      fetchBooks();
      setMergeDialogOpen(false);
    } catch (err) {
      console.error('Failed to merge books:', err);
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Book',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              backgroundColor: `${theme.palette.primary.main}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <BookIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          </Box>
          <Box sx={{ fontWeight: 500, fontSize: '0.8rem' }}>{params.row.name}</Box>
        </Box>
      ),
    },
    {
      field: 'recipes_count',
      headerName: 'Recipes',
      width: 100,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'user_email',
      headerName: 'Owner',
      width: 150,
      renderCell: (params) =>
        params.row.user_email ? (
          <Box sx={{ fontSize: '0.8rem' }}>{params.row.user_email.split('@')[0]}</Box>
        ) : (
          <Box sx={{ fontSize: '0.8rem', color: theme.palette.success.main }}>Public</Box>
        ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      renderCell: (params) =>
        params.row.created_at
          ? format(new Date(params.row.created_at), 'MMM d, yyyy')
          : '-',
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', height: '100%' }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ color: theme.palette.text.secondary }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="books"
        subtitle={`${totalCount} total books`}
        breadcrumbs={[{ label: 'books' }]}
        action={
          selected.length === 2 && (
            <Button
              variant="outlined"
              startIcon={<MergeIcon />}
              onClick={() => setMergeDialogOpen(true)}
            >
              Merge Selected
            </Button>
          )
        }
      />

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          placeholder="Search books..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Data Table */}
      <DataTable
        rows={books}
        columns={columns}
        loading={loading}
        page={page}
        pageSize={pageSize}
        rowCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        checkboxSelection
        onSelectionChange={setSelected}
        getRowId={(row) => row.id}
      />

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Book</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Author"
            value={formData.author}
            onChange={(e) => setFormData({ ...formData, author: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={mergeDialogOpen} onClose={() => setMergeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Merge Books</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            All recipes from the first book will be moved to the second book, and the first book will be deleted.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={5}>
              <Box sx={{ p: 2, backgroundColor: theme.palette.background.elevated, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Source (will be deleted)
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  {books.find((b) => b.id === selected[0])?.name}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MergeIcon sx={{ color: theme.palette.text.secondary }} />
            </Grid>
            <Grid item xs={5}>
              <Box sx={{ p: 2, backgroundColor: theme.palette.background.elevated, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Target (will keep)
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  {books.find((b) => b.id === selected[1])?.name}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleMerge}>
            Merge
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Books;
